import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  LocalNetworkAccessContext,
  type LnaPermissionState,
} from "./lna-context";
import { LnaPermissionDialog } from "@/components/LnaPermissionDialog";

function mapPermissionState(state: PermissionState): LnaPermissionState {
  if (state === "granted") return "granted";
  if (state === "denied") return "denied";
  return "prompt";
}

export function LocalNetworkAccessProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [permissionState, setPermissionState] =
    useState<LnaPermissionState>("unknown");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"prompt" | "denied">("prompt");
  const promiseRef = useRef<{
    resolve: () => void;
    reject: (err: Error) => void;
  } | null>(null);

  // Query permission on mount and listen for changes
  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;

    (async () => {
      try {
        status = await navigator.permissions.query({
          name: "loopback-network" as PermissionName,
        });
        if (cancelled) return;
        setPermissionState(mapPermissionState(status.state));
        status.onchange = () => {
          if (status) setPermissionState(mapPermissionState(status.state));
        };
      } catch {
        if (!cancelled) setPermissionState("unsupported");
      }
    })();

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, []);

  // Auto-resolve/update dialog when permission state changes while dialog is open
  useEffect(() => {
    if (!promiseRef.current) return;

    if (permissionState === "granted") {
      promiseRef.current.resolve();
      promiseRef.current = null;
      setDialogOpen(false);
    } else if (permissionState === "denied") {
      setDialogMode("denied");
    }
  }, [permissionState]);

  const ensurePermission = useCallback(async () => {
    if (permissionState === "granted" || permissionState === "unsupported") {
      return;
    }
    if (permissionState === "denied") {
      // Show denied dialog and wait for user to dismiss
      return new Promise<void>((resolve, reject) => {
        promiseRef.current = { resolve, reject };
        setDialogMode("denied");
        setDialogOpen(true);
      });
    }
    // "prompt" or "unknown" — show informational dialog
    return new Promise<void>((resolve, reject) => {
      promiseRef.current = { resolve, reject };
      setDialogMode("prompt");
      setDialogOpen(true);
    });
  }, [permissionState]);

  const handleContinueToAllow = useCallback(async () => {
    try {
      // This fetch triggers the browser's native LNA permission prompt
      await fetch("http://localhost", { mode: "no-cors" });
    } catch {
      // Expected — the fetch will fail, but it triggers the browser prompt
    }
    // The permission.onchange listener (in the useEffect above) will detect
    // the state change and resolve/reject the promise accordingly.
  }, []);

  const handleDismiss = useCallback(() => {
    setDialogOpen(false);
    if (promiseRef.current) {
      promiseRef.current.reject(new Error("LNA permission dialog dismissed"));
      promiseRef.current = null;
    }
  }, []);

  const value = useMemo(
    () => ({ permissionState, ensurePermission }),
    [permissionState, ensurePermission],
  );

  return (
    <LocalNetworkAccessContext.Provider value={value}>
      {children}
      <LnaPermissionDialog
        open={dialogOpen}
        mode={dialogMode}
        onContinueToAllow={handleContinueToAllow}
        onDismiss={handleDismiss}
      />
    </LocalNetworkAccessContext.Provider>
  );
}
